import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Flex,
  Text,
  Button,
  Badge,
  Checkbox,
  Spinner,
  Alert,
  AlertIcon,
  useColorModeValue,
} from "@chakra-ui/react";
import { getBid } from "../../lib/Auth";
import { ivApi, isOk, isPermissionRefused, ACCESS_BADGES, readinessSentence, SETTINGS_PATH, WORKLIST_PATH } from "./insuranceVerification";
import { PracticeDetails, PayerSearch, PortalRow, PdfToPms, InsuranceQuestions, SectionCard } from "./EligibilitySettings";

/**
 * Enable insurance verification: the guided setup (SS-3944, 2026-10-21). Practice details,
 * your insurers (ranked from claims, with access badges and patient shares), portal logins
 * (only the selected insurers that need one), the PDF and your questions, then the summary.
 * Every step saves through the API; the step reached is remembered so the setup resumes.
 */

export const STEPS = [
  { key: "details", label: "Practice details" },
  { key: "insurers", label: "Your insurers" },
  { key: "portals", label: "Portal logins" },
  { key: "pdf", label: "PDF and questions" },
  { key: "summary", label: "Summary" },
];

const stepKey = (bid) => `iv-setup-step-${bid}`;

/** The portals the setup asks for: required (portal-only insurers) first by patient share, optional apart. */
export const portalLists = (portals, payers) => {
  const shareOf = (portal) => Math.max(0, ...(portal.payers || []).map((p) => payers.find((x) => x.commonPayerId === p.commonPayerId)?.patient_share || 0));
  const withShare = (portals || []).map((p) => ({ ...p, share: shareOf(p) })).sort((a, b) => b.share - a.share);
  return { required: withShare.filter((p) => p.requirement === "required"), optional: withShare.filter((p) => p.requirement !== "required") };
};

function InsurersStep({ setup, onSaved }) {
  const [rows, setRows] = useState(setup?.payers || []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [fixing, setFixing] = useState(null);

  // a refreshed list (after a mapping) keeps the ticks and added insurers not saved yet
  useEffect(() => {
    setRows((local) => {
      const byId = new Map(local.map((r) => [r.commonPayerId, r]));
      const fresh = (setup?.payers || []).map((r) => (byId.has(r.commonPayerId) ? { ...r, selected: byId.get(r.commonPayerId).selected } : r));
      const freshIds = new Set(fresh.map((r) => r.commonPayerId));
      return [...fresh, ...local.filter((r) => r.source === "manual" && !freshIds.has(r.commonPayerId))];
    });
  }, [setup]);

  const toggle = (id, selected) => setRows((rs) => rs.map((r) => (r.commonPayerId === id ? { ...r, selected } : r)));
  const addPayer = (p) => setRows((rs) => (rs.some((r) => r.commonPayerId === p.commonPayerId)
    ? rs.map((r) => (r.commonPayerId === p.commonPayerId ? { ...r, selected: true } : r))
    : [...rs, { commonPayerId: p.commonPayerId, name: p.name, access: p.access || "none", selected: true, source: "manual", patient_share: 0, carrier_names: [] }]));

  const remap = async (row, payer) => {
    const entries = (row.carrier_names || []).map((n) => ({ carrier_name: n, commonPayerId: payer.commonPayerId }));
    if (entries.length) {
      const r = await ivApi.putPayerMap(entries);
      if (!isOk(r)) {
        setError(r?.msg || "Could not save the match");
        return;
      }
    }
    setFixing(null);
    onSaved();
  };
  const confirmUnmapped = async (u, payer) => {
    const r = await ivApi.putPayerMap([{ carrier_name: u.carrier_name, commonPayerId: payer.commonPayerId }]);
    if (isOk(r)) onSaved();
    else setError(r?.msg || "Could not save the match");
  };

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const r = await ivApi.putSettings({ payers: rows.map((x) => ({ commonPayerId: x.commonPayerId, selected: x.selected !== false, source: x.source || "claims" })) });
      if (isOk(r)) onSaved(true);
      else setError(r?.msg || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <SectionCard title="Your insurers" subtitle="The insurers on your claims, most patients first. Untick the ones you no longer accept." testId="iv-setup-insurers">
      {rows.map((r) => {
        const badge = ACCESS_BADGES[r.access] || ACCESS_BADGES.none;
        return (
          <Box key={r.commonPayerId} py="2" borderBottom="1px solid #E5E5EF" data-testid="iv-setup-payer">
            <Flex justify="space-between" align="center" gap="2" wrap="wrap">
              <Checkbox isChecked={r.selected !== false} onChange={(e) => toggle(r.commonPayerId, e.target.checked)} data-testid="iv-setup-payer-selected">
                <Text fontSize="13px" fontWeight="600">{r.name || r.commonPayerId}</Text>
              </Checkbox>
              <Flex gap="2" align="center" wrap="wrap">
                <Badge colorScheme={badge.color} textTransform="none" data-testid="iv-access-badge">{badge.label}</Badge>
                <Text fontSize="12px" color="#64728C" data-testid="iv-patient-share">{r.patient_share}% of patients</Text>
              </Flex>
            </Flex>
            {(r.carrier_names || []).length > 0 && (
              <Flex fontSize="12px" color="#64728C" gap="2" mt="1" align="center" wrap="wrap">
                <Text>In your PMS as: {r.carrier_names.join(", ")}</Text>
                <Button size="xs" variant="link" color="#8E4997" onClick={() => setFixing(fixing === r.commonPayerId ? null : r.commonPayerId)}>Not this insurer? Fix</Button>
              </Flex>
            )}
            {fixing === r.commonPayerId && <Box mt="2"><PayerSearch onPick={(p) => remap(r, p)} /></Box>}
          </Box>
        );
      })}
      {(setup?.unmapped || []).length > 0 && (
        <Box mt="4">
          <Text fontSize="13px" fontWeight="700" mb="1">Insurer names to match</Text>
          {setup.unmapped.map((u) => (
            <Box key={u.carrier_name || u.carrier_payer_id} py="2" borderBottom="1px solid #E5E5EF" data-testid="iv-setup-unmapped">
              <Flex justify="space-between" fontSize="13px"><Text>{u.carrier_name || u.carrier_payer_id}</Text><Text color="#64728C">{u.patient_share}% of patients</Text></Flex>
              <Flex gap="2" wrap="wrap" my="1">
                {(u.suggestions || []).map((s) => (
                  <Button key={s.commonPayerId} size="xs" variant="outline" onClick={() => confirmUnmapped(u, s)}>{s.name}</Button>
                ))}
              </Flex>
              <PayerSearch onPick={(p) => confirmUnmapped(u, p)} placeholder="Search the insurer for this name" />
            </Box>
          ))}
        </Box>
      )}
      <Box mt="4">
        <Text fontSize="13px" fontWeight="700" mb="1">Add an insurer you accept</Text>
        <PayerSearch onPick={addPayer} />
      </Box>
      {error && <Text fontSize="13px" color="#B3261E" mt="2">{error}</Text>}
      <Button mt="4" size="sm" bg="#154043" color="#F9F9FB" _hover={{ bg: "#123536" }} onClick={save} isLoading={saving} data-testid="iv-setup-insurers-save">Save and continue</Button>
    </SectionCard>
  );
}

function PortalsStep({ setup, onNext }) {
  const [portals, setPortals] = useState(null);
  const load = async () => {
    const r = await ivApi.getPortals();
    setPortals(isOk(r) ? r.data || [] : []);
  };
  useEffect(() => { load(); }, []);
  const { required, optional } = useMemo(() => portalLists(portals || [], setup?.payers || []), [portals, setup]);
  return (
    <SectionCard title="Portal logins" subtitle="Only for your insurers that need one. Skip any: a skipped insurer gets basic eligibility." testId="iv-setup-portals">
      {!portals && <Spinner size="sm" />}
      {portals && required.length === 0 && <Text fontSize="13px" color="#64728C">None of your insurers needs a portal login.</Text>}
      <Box data-testid="iv-setup-portals-required">
        {required.map((p) => <PortalRow key={p.portalId} portal={p} share={p.share} onChanged={load} />)}
      </Box>
      {optional.length > 0 && (
        <Box mt="4" data-testid="iv-setup-portals-optional">
          <Text fontSize="13px" fontWeight="700" mb="1">Optional logins</Text>
          <Text fontSize="12px" color="#64728C" mb="1">These insurers answer without a login; a login can add detail.</Text>
          {optional.map((p) => <PortalRow key={p.portalId} portal={p} share={p.share} onChanged={load} />)}
        </Box>
      )}
      <Button mt="4" size="sm" bg="#154043" color="#F9F9FB" _hover={{ bg: "#123536" }} onClick={onNext} data-testid="iv-setup-portals-next">Continue</Button>
    </SectionCard>
  );
}

function SummaryStep({ setup, settings, onEnable, enabling, error }) {
  const bid = getBid();
  return (
    <SectionCard title="Summary" testId="iv-setup-summary">
      <Text fontSize="15px" fontWeight="600" mb="3" data-testid="iv-readiness">{readinessSentence(setup?.readiness)}</Text>
      <Text fontSize="13px" color="#64728C" mb="3">
        {"Next: run a check from a patient's Insurance card in the Inbox, or check your upcoming appointments from the worklist."}
        {(setup?.readiness?.full_with_login?.payers || []).length > 0 && " Connect the skipped portals any time in Settings."}
      </Text>
      {error && <Alert status="warning" borderRadius="8px" mb="3" fontSize="13px"><AlertIcon />{error}</Alert>}
      <Flex gap="3" wrap="wrap">
        {!settings?.enabled && (
          <Button size="sm" bg="#154043" color="#F9F9FB" _hover={{ bg: "#123536" }} onClick={onEnable} isLoading={enabling} data-testid="iv-setup-enable">
            Turn on insurance verification
          </Button>
        )}
        {settings?.enabled && <Badge colorScheme="green" alignSelf="center">Insurance verification is on</Badge>}
        <Button as="a" href={`${WORKLIST_PATH}?bid=${bid}`} size="sm" variant="outline">Open the worklist</Button>
        <Button as="a" href={`${SETTINGS_PATH}?bid=${bid}`} size="sm" variant="ghost">Settings</Button>
      </Flex>
    </SectionCard>
  );
}

export default function EligibilitySetup() {
  const bid = getBid();
  const location = useLocation();
  const navigate = useNavigate();
  const requested = new URLSearchParams(location.search).get("step");
  const initial = Math.max(0, STEPS.findIndex((s) => s.key === (requested || localStorage.getItem(stepKey(bid)) || "details")));
  const [step, setStep] = useState(initial);
  const [settings, setSettings] = useState(null);
  const [setup, setSetup] = useState(null);
  const [error, setError] = useState("");
  const [enabling, setEnabling] = useState(false);
  const [adminOnly, setAdminOnly] = useState(false);
  const subColor = useColorModeValue("#64728C", "#A9B1C2");

  const loadSetup = async () => {
    const r = await ivApi.setupPayers();
    if (isOk(r)) setSetup(r.data);
    // the API's permission refusal: a staff member who followed the card's "Connect the … portal"
    else if (isPermissionRefused(r)) setAdminOnly(true);
  };
  useEffect(() => {
    ivApi.getSettings().then((r) => {
      if (isOk(r)) setSettings(r.data);
      else setError(r?.msg || "The setup is for practice admins.");
    });
    loadSetup();
  }, []);

  const go = (i) => {
    setStep(i);
    localStorage.setItem(stepKey(bid), STEPS[i].key);
    if (requested) navigate(`${location.pathname}?bid=${bid}`, { replace: true });
  };
  const next = () => go(Math.min(step + 1, STEPS.length - 1));

  const enable = async () => {
    setEnabling(true);
    setError("");
    try {
      const r = await ivApi.putSettings({ enabled: true, setup_zuub: true });
      if (isOk(r)) setSettings(r.data);
      else setError([r?.msg, ...(r?.missing || [])].filter(Boolean).join(" — "));
    } finally {
      setEnabling(false);
    }
  };

  if (adminOnly) {
    return (
      <Box p="6" data-testid="iv-setup-admin-only">
        <Alert status="info" borderRadius="8px"><AlertIcon />
          {"Setting up insurance verification, and connecting an insurer's portal, is for your practice admins. Ask one of them to connect the portal."}
        </Alert>
      </Box>
    );
  }
  if (!settings) {
    return error ? <Box p="6"><Alert status="warning" borderRadius="8px"><AlertIcon />{error}</Alert></Box> : <Flex p="10" justify="center"><Spinner /></Flex>;
  }

  return (
    <Box p={{ base: 3, md: 6 }} maxW="900px" w="100%" overflowY="auto" data-testid="iv-setup">
      <Text fontSize="20px" fontWeight="700">Enable insurance verification</Text>
      <Flex gap="2" my="4" wrap="wrap" data-testid="iv-setup-steps">
        {STEPS.map((s, i) => (
          <Button key={s.key} size="xs" variant={i === step ? "solid" : "ghost"} colorScheme={i === step ? "teal" : undefined} onClick={() => go(i)}>
            {i + 1}. {s.label}
          </Button>
        ))}
      </Flex>
      {STEPS[step].key === "details" && (
        <PracticeDetails settings={settings} saveLabel="Save and continue" onSaved={(s) => { setSettings(s); next(); }} />
      )}
      {STEPS[step].key === "insurers" && (
        setup ? <InsurersStep setup={setup} onSaved={(advance) => { loadSetup(); if (advance === true) next(); }} /> : <Spinner size="sm" />
      )}
      {STEPS[step].key === "portals" && <PortalsStep setup={setup} onNext={next} />}
      {STEPS[step].key === "pdf" && (
        <>
          <PdfToPms settings={settings} onSaved={setSettings} />
          <InsuranceQuestions settings={settings} onSaved={setSettings} />
          <Button size="sm" bg="#154043" color="#F9F9FB" _hover={{ bg: "#123536" }} onClick={() => { loadSetup(); next(); }} data-testid="iv-setup-pdf-next">Continue</Button>
        </>
      )}
      {STEPS[step].key === "summary" && <SummaryStep setup={setup} settings={settings} onEnable={enable} enabling={enabling} error={error} />}
      <Text fontSize="12px" color={subColor} mt="3">Each step is saved as you go; you can come back and finish later.</Text>
    </Box>
  );
}
